import { useState, type ReactNode } from "react";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { SelectInputField } from "@/components/common/SelectInputField";
import { Spinner } from "@/components/common/Spinner";
import {
	runnerPackageDownloadUrl,
	runnerPackageSourcesUrl,
	type RunnerArch,
	type RunnerPackage,
} from "@/lib/api";
import { ACTION_LINK_CLASS } from "@/lib/link-styles";

const ARCH_LABELS: Record<RunnerArch, string> = {
	amd64: "x86_64 (Intel・AMD)",
	arm64: "arm64 (DGX Spark など)",
};

/** select の高さ (.ds-select)。ボタンを select の縦中央に揃える。 */
const SELECT_HEIGHT = 36;

interface RunnerPackageDownloadProps {
	/** undefined は取得中、null はランナーの版か公開 URL が未設定で配れない (backend の 503)。 */
	release: RunnerPackage | null | undefined;
	/** 取得の失敗。再試行を出す。 */
	error: string | null;
	onRetry: () => void;
	/** .deb のダウンロードだけを止める。ソースコードは配り続ける。 */
	disabled?: boolean;
	/** ソースコードのリンクの後ろに同じ行で並べるもの。 */
	children?: ReactNode;
}

/** デバイスに入れる .deb のダウンロード。CPU を選んでダウンロードし、ソースコードも添える。 */
export function RunnerPackageDownload({
	release,
	error,
	onRetry,
	disabled = false,
	children,
}: RunnerPackageDownloadProps) {
	if (error)
		return (
			<span style={{ display: "inline-flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
				<span data-testid="runner-package-error" style={{ color: "var(--danger)" }}>
					{error}
				</span>
				<ConfirmButton
					label="再試行"
					loading={false}
					variant="secondary"
					size="sm"
					onClick={onRetry}
					data-testid="runner-package-retry"
				/>
			</span>
		);
	if (release === undefined) return <Spinner size={12} data-testid="runner-package-loading" />;
	if (release === null)
		return (
			<span data-testid="runner-package-unavailable" style={{ color: "var(--fg-2)" }}>
				ランナーの版か公開 URL が設定されていないため、ダウンロードできません。
			</span>
		);
	return (
		<PackageChooser release={release} disabled={disabled}>
			{children}
		</PackageChooser>
	);
}

function PackageChooser({
	release,
	disabled,
	children,
}: {
	release: RunnerPackage;
	disabled: boolean;
	children: ReactNode;
}) {
	const [arch, setArch] = useState<RunnerArch>(() =>
		release.architectures.includes("arm64") ? "arm64" : release.architectures[0],
	);
	return (
		<div
			data-testid="runner-package-downloads"
			style={{ display: "flex", flexDirection: "column", gap: 6 }}
		>
			<div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 8 }}>
				<div style={{ width: 240 }}>
					<SelectInputField
						label="デバイスの CPU"
						value={arch}
						onChange={(value) => setArch(value as RunnerArch)}
						testId="runner-package-arch"
					>
						{release.architectures.map((option) => (
							<option key={option} value={option}>
								{ARCH_LABELS[option]}
							</option>
						))}
					</SelectInputField>
				</div>
				<div style={{ display: "flex", alignItems: "center", gap: 8, height: SELECT_HEIGHT }}>
					<a
						href={disabled ? undefined : runnerPackageDownloadUrl(arch)}
						download={disabled ? undefined : true}
						data-tooltip={
							disabled ? "空いているライセンスが無いためダウンロードできません" : undefined
						}
						data-testid="runner-package-download"
						className={
							disabled
								? "bg-accent-solid text-on-accent-solid"
								: "bg-accent-solid text-on-accent-solid transition-colors hover:bg-accent-solid-hover active:brightness-90"
						}
						style={{
							cursor: disabled ? "not-allowed" : undefined,
							opacity: disabled ? 0.5 : undefined,
							height: 28,
							padding: "0 12px",
							display: "inline-flex",
							alignItems: "center",
							borderRadius: "var(--radius-2)",
							fontSize: "var(--text-caption)",
							fontWeight: 500,
							textDecoration: "none",
							whiteSpace: "nowrap",
						}}
					>
						ダウンロード
					</a>
					<span style={{ color: "var(--fg-3)", fontSize: "var(--text-caption)" }}>
						(版 {release.version})
					</span>
				</div>
			</div>
			<p style={{ margin: 0, color: "var(--fg-3)", fontSize: "var(--text-caption)" }}>
				<a
					href={runnerPackageSourcesUrl()}
					download
					data-testid="runner-package-sources"
					className={`whitespace-nowrap ${ACTION_LINK_CLASS}`}
				>
					ソースコード (GPL など)
				</a>
				{children}
			</p>
		</div>
	);
}
